import { c as _c } from "react/compiler-runtime";
import Link from "@/components/DashboardLink";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { Blocks, FolderKanban, Layers, Slack, SquarePen, Zap, type LucideIcon } from "lucide-react";
import { ShortcutKeys } from "@/components/ui/shortcut-keys";
import { Badge } from "@repo/blacksmith-ui/badge";
import { cn } from "@repo/blacksmith-ui/cn";
import { Skeleton } from "@repo/blacksmith-ui/skeleton";
import { useIsHydrated } from "./skeletonLayout";
import { readCachedAutomationsEnabled } from "./useCachedAutomationsEnabled";
import { readCachedProjectsEnabled } from "./useCachedProjectsEnabled";
import type { CodesmithSidebarVariant } from "./sidebarVariant";
import { REDESIGN_ROW_FILL_INSET_CLASS } from "./compactSessionRowStyles";
import { FOCUS_BORDER_CLASS } from "./focusStyles";

/**
 * Primary navigation above the session lists. The New session row stays
 * pinned under the header; the subpage rows scroll away with the sessions.
 */
export type SidebarPrimaryNavRows = "all" | "new-session" | "subpages";
const NAV_ROW = cn("group flex h-8 w-full items-center gap-2 rounded-md border border-transparent p-1.5 type-body-s-regular cursor-pointer transition-none text-neutral-800 hover:bg-black/[0.04] hover:text-neutral-950 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white", FOCUS_BORDER_CLASS);
const NAV_ROW_ACTIVE = "bg-[#0000000f] text-neutral-950 [&_svg]:text-neutral-900 dark:bg-white/6 dark:text-white dark:[&_svg]:text-white";
const REDESIGN_NAV_ROW = cn("group flex h-8 w-full items-center gap-1.5 rounded-[4px] px-2 type-body-s-regular cursor-pointer transition-none text-neutral-700 hover:bg-black/[0.04] hover:text-neutral-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--focus-border)] [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white", REDESIGN_ROW_FILL_INSET_CLASS);

// The "subpages" nav lives inside the session list's scroll container, whose
// wrapper already pads horizontally (px-2 / pl-2) and, in legacy, adds a pt-1
// that stands in for the gap-1 between New session and the first subpage row.
const NAV_CLASS: Record<CodesmithSidebarVariant, Record<SidebarPrimaryNavRows, string>> = {
  legacy: {
    all: "flex flex-col gap-1 px-2 pt-[calc(var(--spacing-app-header-gap)-0.5rem)] pb-2",
    "new-session": "flex flex-col gap-1 px-2 pt-[calc(var(--spacing-app-header-gap)-0.5rem)]",
    subpages: "flex flex-col gap-1 pb-3"
  },
  redesign: {
    all: "flex flex-col px-2 pb-4",
    "new-session": "flex flex-col px-2",
    subpages: "flex flex-col pb-4"
  }
};

// Split navs need distinct names so landmark navigation can tell them apart.
const NAV_LABEL: Record<SidebarPrimaryNavRows, string | undefined> = {
  all: undefined,
  "new-session": "New session",
  subpages: "Pages"
};
function navClasses(variant: CodesmithSidebarVariant, rows: SidebarPrimaryNavRows) {
  return {
    label: NAV_LABEL[rows],
    nav: NAV_CLASS[variant][rows],
    row: variant === "redesign" ? REDESIGN_NAV_ROW : NAV_ROW,
    icon: variant === "redesign" ? "size-4 shrink-0" : "size-[18px] shrink-0"
  };
}

// Paint-only fade under the overlaid keycaps so the label never re-lays out;
// 4.5rem clears all three keycaps.
const LABEL_UNDER_CHIP_FADE_CLASS = "group-hover:[mask-image:linear-gradient(to_left,transparent_4.5rem,black_5.5rem)] group-focus-visible:[mask-image:linear-gradient(to_left,transparent_4.5rem,black_5.5rem)]";
function ShortcutChip(t0) {
  const $ = _c(2);
  const {
    shortcut
  } = t0;
  let t1;
  if ($[0] !== shortcut) {
    t1 = <ShortcutKeys keys={Array.from(shortcut)} className="absolute top-1/2 right-0 -translate-y-1/2 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100" />;
    $[0] = shortcut;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function RowContents(t0) {
  const $ = _c(21);
  const {
    icon: Icon,
    label,
    shortcut,
    alpha: t1,
    iconClassName: t2
  } = t0;
  const alpha = t1 === undefined ? false : t1;
  const iconClassName = t2 === undefined ? "size-[18px] shrink-0" : t2;
  let t3;
  if ($[0] !== Icon || $[1] !== iconClassName) {
    t3 = <Icon className={iconClassName} />;
    $[0] = Icon;
    $[1] = iconClassName;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = shortcut && LABEL_UNDER_CHIP_FADE_CLASS;
  let t5;
  if ($[3] !== t4) {
    t5 = cn("flex h-[22px] min-w-0 flex-wrap items-center gap-x-1.5 overflow-hidden", t4);
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== label) {
    t6 = <span className="truncate text-left">{label}</span>;
    $[5] = label;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== alpha) {
    t7 = alpha ? <Badge variant="muted" className="shrink-0">Alpha</Badge> : null;
    $[7] = alpha;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t5 || $[10] !== t6 || $[11] !== t7) {
    t8 = <div className={t5}>{t6}{t7}</div>;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== shortcut) {
    t9 = shortcut ? <ShortcutChip shortcut={shortcut} /> : null;
    $[13] = shortcut;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t8 || $[16] !== t9) {
    t10 = <div className="relative min-w-0 flex-1">{t8}{t9}</div>;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t3) {
    t11 = <>{t3}{t10}</>;
    $[18] = t10;
    $[19] = t3;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
}
export interface SidebarPrimaryNavProps {
  /** Starts a new Codesmith session (same action as the old toolbar new-chat). */
  onNewSession: () => void;
  /** Resolved automations route, or null/undefined to hide the row (flag off). */
  automationsHref?: string | null;
  onPrefetchAutomations?: () => void;
  /** Resolved projects route, or null/undefined to hide the row (flag off). */
  projectsHref?: string | null;
  /** Resolved MCP Connections route. */
  mcpConnectionsHref: string;
  /** Once the org's Slack workspace has a DM link the row reads "Open in Slack". */
  slackConnected?: boolean;
  /** Holds a placeholder in the Slack row until its connection status resolves. */
  slackLoading?: boolean;
  /**
   * Called after a subpage row navigates (e.g. to collapse the sidebar on
   * mobile). The New session row deliberately does NOT call this: its mobile
   * collapse and write-blocked gating are owned by `onNewSession`, so invoking
   * `onNavigate` there too would double-fire the collapse (and on mobile toggle
   * the sidebar back open).
   */
  onNavigate?: () => void;
  newSessionShortcut?: string;
  automationsShortcut?: string;
  /** Highlights the row matching the current Codesmith subpage. */
  activeRow?: "new-session" | "connect-slack" | "automations" | "projects" | "mcp-connections" | "machine-definitions" | null;
  className?: string;
  variant?: CodesmithSidebarVariant;
  /** Which rows this nav renders; the sidebar splits them across two navs. */
  rows?: SidebarPrimaryNavRows;
}
export function SidebarPrimaryNav(t0) {
  const $ = _c(26);
  const {
    onNewSession,
    automationsHref,
    onPrefetchAutomations,
    projectsHref,
    mcpConnectionsHref,
    slackConnected: t1,
    slackLoading: t2,
    onNavigate,
    newSessionShortcut: t3,
    automationsShortcut: t4,
    activeRow: t5,
    className,
    variant: t6,
    rows: t7
  } = t0;
  const slackConnected = t1 === undefined ? false : t1;
  const slackLoading = t2 === undefined ? false : t2;
  const newSessionShortcut = t3 === undefined ? "\u2325\u21E7N" : t3;
  const automationsShortcut = t4 === undefined ? "\u2325\u21E7A" : t4;
  const activeRow = t5 === undefined ? null : t5;
  const variant = t6 === undefined ? "legacy" : t6;
  const rows = t7 === undefined ? "all" : t7;
  const router = useRouter();
  const org = router.query.org;
  const newSessionHref = typeof org === "string" ? `/${org}/sessions/new` : "/sessions/new";
  const machineDefinitionsHref = typeof org === "string" ? `/${org}/codesmith/machine-definitions` : "/codesmith/machine-definitions";
  const connectSlackHref = typeof org === "string" ? `/${org}/codesmith/connect-slack` : "/codesmith/connect-slack";
  let t10;
  let t11;
  let t8;
  let t9;
  if ($[0] !== activeRow || $[1] !== automationsHref || $[2] !== automationsShortcut || $[3] !== className || $[4] !== connectSlackHref || $[5] !== machineDefinitionsHref || $[6] !== mcpConnectionsHref || $[7] !== newSessionHref || $[8] !== newSessionShortcut || $[9] !== onNavigate || $[10] !== onNewSession || $[11] !== onPrefetchAutomations || $[12] !== projectsHref || $[13] !== rows || $[14] !== slackConnected || $[15] !== slackLoading || $[16] !== variant) {
    const classes = navClasses(variant, rows);
    const NAV = classes.row;
    const redesign = variant === "redesign";
    const showNewSession = rows !== "subpages";
    const showSubpages = rows !== "new-session";
    t8 = classes.label;
    t9 = cn(classes.nav, className);
    t10 = showNewSession && <Link href={newSessionHref} onClick={e => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      e.preventDefault();
      onNewSession();
    }} aria-keyshortcuts="Alt+Shift+N" aria-current={activeRow === "new-session" ? "page" : undefined} className={cn(NAV, activeRow === "new-session" && NAV_ROW_ACTIVE)}><RowContents icon={SquarePen} label={redesign ? "New Chat" : "New session"} shortcut={newSessionShortcut} iconClassName={classes.icon} /></Link>;
    t11 = showSubpages && <>{slackLoading ? <div className={NAV} aria-label="Loading Slack connection"><Slack className={classes.icon} /><Skeleton className="h-3 w-24 rounded" /></div> : <Link href={connectSlackHref} onClick={onNavigate} aria-current={activeRow === "connect-slack" ? "page" : undefined} className={cn(NAV, activeRow === "connect-slack" && NAV_ROW_ACTIVE)}><RowContents icon={Slack} label={slackConnected ? "Open in Slack" : "Connect Slack"} iconClassName={classes.icon} /></Link>}{automationsHref ? <Link href={automationsHref} onMouseEnter={onPrefetchAutomations} onFocus={onPrefetchAutomations} onClick={onNavigate} aria-keyshortcuts="Alt+Shift+A" aria-current={activeRow === "automations" ? "page" : undefined} className={cn(NAV, activeRow === "automations" && NAV_ROW_ACTIVE)}><RowContents icon={Zap} label="Automations" alpha={true} shortcut={automationsShortcut} iconClassName={classes.icon} /></Link> : null}{projectsHref ? <Link href={projectsHref} onClick={onNavigate} aria-current={activeRow === "projects" ? "page" : undefined} className={cn(NAV, activeRow === "projects" && NAV_ROW_ACTIVE)}><RowContents icon={FolderKanban} label="Projects" alpha={true} iconClassName={classes.icon} /></Link> : null}<Link href={mcpConnectionsHref} onClick={onNavigate} aria-current={activeRow === "mcp-connections" ? "page" : undefined} className={cn(NAV, activeRow === "mcp-connections" && NAV_ROW_ACTIVE)}><RowContents icon={Blocks} label="MCP Connections" iconClassName={classes.icon} /></Link><Link href={machineDefinitionsHref} onClick={onNavigate} aria-current={activeRow === "machine-definitions" ? "page" : undefined} className={cn(NAV, activeRow === "machine-definitions" && NAV_ROW_ACTIVE)}><RowContents icon={Layers} label="Machine Definitions" iconClassName={classes.icon} /></Link></>;
    $[0] = activeRow;
    $[1] = automationsHref;
    $[2] = automationsShortcut;
    $[3] = className;
    $[4] = connectSlackHref;
    $[5] = machineDefinitionsHref;
    $[6] = mcpConnectionsHref;
    $[7] = newSessionHref;
    $[8] = newSessionShortcut;
    $[9] = onNavigate;
    $[10] = onNewSession;
    $[11] = onPrefetchAutomations;
    $[12] = projectsHref;
    $[13] = rows;
    $[14] = slackConnected;
    $[15] = slackLoading;
    $[16] = variant;
    $[17] = t10;
    $[18] = t11;
    $[19] = t8;
    $[20] = t9;
  } else {
    t10 = $[17];
    t11 = $[18];
    t8 = $[19];
    t9 = $[20];
  }
  let t12;
  if ($[21] !== t10 || $[22] !== t11 || $[23] !== t8 || $[24] !== t9) {
    t12 = <nav aria-label={t8} className={t9}>{t10}{t11}</nav>;
    $[21] = t10;
    $[22] = t11;
    $[23] = t8;
    $[24] = t9;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  return t12;
}

/**
 * Non-interactive twin of SidebarPrimaryNav for the sidebar skeleton: these
 * rows are static chrome, so the skeleton renders them for real instead of
 * shimmer blocks and the top of the sidebar doesn't shift or pop when the
 * live sidebar mounts. The Automations row mirrors the live sidebar's
 * pre-flag fallback by reading the same per-org cache.
 */
export function SidebarPrimaryNavStatic(t0) {
  const $ = _c(27);
  const {
    className,
    variant: t1,
    rows: t2
  } = t0;
  const variant = t1 === undefined ? "legacy" : t1;
  const rows = t2 === undefined ? "all" : t2;
  const router = useRouter();
  const org = router.query.org;
  const isHydrated = useIsHydrated();
  const orgName = typeof org === "string" ? org : null;
  let t3;
  if ($[0] !== isHydrated || $[1] !== orgName) {
    t3 = isHydrated && readCachedAutomationsEnabled(orgName);
    $[0] = isHydrated;
    $[1] = orgName;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const showAutomations = t3;
  let t4;
  if ($[3] !== isHydrated || $[4] !== orgName) {
    t4 = isHydrated && readCachedProjectsEnabled(orgName);
    $[3] = isHydrated;
    $[4] = orgName;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const showProjects = t4;
  let classes;
  let redesign;
  let t5;
  let t6;
  if ($[6] !== className || $[7] !== rows || $[8] !== variant) {
    classes = navClasses(variant, rows);
    redesign = variant === "redesign";
    t5 = classes.label;
    t6 = cn(classes.nav, className);
    $[6] = className;
    $[7] = rows;
    $[8] = variant;
    $[9] = classes;
    $[10] = redesign;
    $[11] = t5;
    $[12] = t6;
  } else {
    classes = $[9];
    redesign = $[10];
    t5 = $[11];
    t6 = $[12];
  }
  let t7;
  if ($[13] !== classes || $[14] !== redesign || $[15] !== rows) {
    t7 = rows !== "subpages" && <div className={classes.row}><RowContents icon={SquarePen} label={redesign ? "New Chat" : "New session"} iconClassName={classes.icon} /></div>;
    $[13] = classes;
    $[14] = redesign;
    $[15] = rows;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== classes || $[18] !== rows || $[19] !== showAutomations || $[20] !== showProjects) {
    t8 = rows !== "new-session" && <><div className={classes.row} aria-label="Loading Slack connection"><Slack className={classes.icon} /><Skeleton className="h-3 w-24 rounded" /></div>{showAutomations && <div className={classes.row}><RowContents icon={Zap} label="Automations" alpha={true} iconClassName={classes.icon} /></div>}{showProjects && <div className={classes.row}><RowContents icon={FolderKanban} label="Projects" alpha={true} iconClassName={classes.icon} /></div>}<div className={classes.row}><RowContents icon={Blocks} label="MCP Connections" iconClassName={classes.icon} /></div><div className={classes.row}><RowContents icon={Layers} label="Machine Definitions" iconClassName={classes.icon} /></div></>;
    $[17] = classes;
    $[18] = rows;
    $[19] = showAutomations;
    $[20] = showProjects;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t5 || $[23] !== t6 || $[24] !== t7 || $[25] !== t8) {
    t9 = <nav aria-label={t5} className={t6}>{t7}{t8}</nav>;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
}